import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'

import {
  Box,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Contact-data widget settings. Composite value under one key (`details_contact`):
//   { fields: Record<string,boolean>, hide_empty: boolean, edit: boolean }
// - `fields`    : per-row show/hide (email, phone_fixed, phone_mobile, address,
//                 country). Pure two-state (shown/hidden), like actions. No
//                 module gating, so no 'disabled' state.
// - `hide_empty`: widget-level toggle. On by default — rows whose value is
//                 empty/blank are skipped at render time.
// - `edit`      : on/off for the bottom \"edit contact\" CTA (relocated from the
//                 old top-right icon-button). On by default. Independent of the
//                 `edit_contact` item mirrored in the actions widget.
// No order setting — the 5 fields render in a fixed natural order. If that's
// ever requested, add an order key + a helper branch.

export type DetailsContactSettingsValue = {
  fields?: Record<string, boolean>;
  hide_empty?: boolean;
  edit?: boolean;
};

type Props = {
  value: DetailsContactSettingsValue;
  onChange: (v: DetailsContactSettingsValue) => void;
  // Unused for this widget (no order setting). Kept in the shared
  // SettingsProps shape so the dialog renders it uniformly.
  order?: string[];
  onOrderChange?: (v: string[]) => void;
};

const LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.details_contact';

export const CoachSettingsWidgetDetailsContact: FunctionComponent<Props> = ({
  value,
  onChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  // Defensive defaults so the dialog works before the first save.
  const fields = value?.fields || {};
  const hideEmpty = value?.hide_empty !== false; // default on
  const edit = value?.edit !== false;            // default on

  // Fixed-order field items, each tagged with shown/hidden state.
  const orderedItems = HelpersClientWidgets.getOrderedDetailsContactFields(fields);

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Field list: pure show/hide. No order, so no drag handles. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.fields.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        {orderedItems.map((item) => {
          const { Icon } = item;
          const checked = item.state === 'shown';
          const stateColor = checked
            ? theme.palette.primary.main
            : theme.palette.secondary.main;
          return (
            <Box
              key={item.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: 1,
                borderRadius: 0.5,
                marginTop: 0.5,
              }}
            >
              <Icon sx={{fontSize: '1.2rem', color: stateColor}} />
              <Switcher
                label={t(`${LABEL_BASE}.fields.items.${item.key}`)}
                value={checked}
                greying
                onChange={(c) => onChange({ ...value, fields: { ...fields, [item.key]: c } })}
              />
            </Box>
          );
        })}
      </Paper>
    </Grid>

    <Grid item xs={12}>
      <Divider />
    </Grid>

    {/* Widget-level toggles. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.options.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.detailsContactHideEmptyIcon
            sx={{fontSize: '1.2rem', color: hideEmpty ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.hide_empty`)}
            value={hideEmpty}
            greying
            onChange={(c) => onChange({ ...value, hide_empty: c })}
          />
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.detailsContactEditIcon
            sx={{fontSize: '1.2rem', color: edit ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.edit`)}
            value={edit}
            greying
            onChange={(c) => onChange({ ...value, edit: c })}
          />
        </Box>
      </Paper>
    </Grid>
  </Grid>;
}

// Read-only status: field icons coloured by shown/hidden + a compact marker
// for the two widget-level toggles (hide_empty / edit) so the placed box
// summarises the whole config at a glance.
type StatusProps = {
  value: DetailsContactSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetDetailsContactStatus: FunctionComponent<StatusProps> = ({
  value
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  const fields = value?.fields || {};
  const hideEmpty = value?.hide_empty !== false; // default on
  const edit = value?.edit !== false;            // default on

  const orderedItems = HelpersClientWidgets.getOrderedDetailsContactFields(fields);

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      {orderedItems.map((item) => {
        const { Icon } = item;
        const color = item.state === 'shown'
          ? theme.palette.primary.main
          : theme.palette.secondary.main;
        return <Box
          key={item.key}
          title={t(`${LABEL_BASE}.fields.items.${item.key}`)}
        >
          <Icon sx={{ fontSize: '1.1rem', color }} />
        </Box>;
      })}
    </Box>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      <Box title={t(`${LABEL_BASE}.options.hide_empty`)}>
        <HelpersClientWidgets.detailsContactHideEmptyIcon
          sx={{ fontSize: '1.1rem', color: hideEmpty ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
      <Box title={t(`${LABEL_BASE}.options.edit`)}>
        <HelpersClientWidgets.detailsContactEditIcon
          sx={{ fontSize: '1.1rem', color: edit ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
    </Box>
  </Box>;
}
